/**
 * Deux usages distincts du rattrapage en délibération :
 *
 * 1. **Source cohorte rattrapage** (`getRattrapageSourcePremiere`) — workflow 2e délibération :
 *    filtrage cohorte, final_note_type, moyenne_ec_rattrapage. Passer l'id de la 2e délibération
 *    (le backend résout la 1re en interne ; l'accès est vérifié sur la délib. passée en URL).
 *
 * 2. **Issue de la délibération** (`getRattrapageDeliberationOutcome`) — feuille / liste historique :
 *    ecs_rattrapage résiduels à l'issue de LA délibération consultée (1re ou 2e).
 */

export function unwrapApiEnvelope<T = unknown>(raw: unknown): T | null {
	if (raw == null) return null
	if (typeof raw === 'object' && raw !== null && 'data' in (raw as object)) {
		return (raw as { data: T }).data
	}
	return raw as T
}

/** Snapshot : source 1re (workflow). Fallback sur l'ancienne clé unique pour snapshots legacy. */
export function unwrapSnapshotRattrapageSourcePremiere(snapshotRaw: Record<string, unknown> | null | undefined): unknown[] | null {
	if (!snapshotRaw) return null
	const sourcePremiere = unwrapApiEnvelope<unknown[]>(
		snapshotRaw.deliberation_etudiants_rattrapage_source_premiere_get
	)
	if (Array.isArray(sourcePremiere)) return sourcePremiere
	const legacy = unwrapApiEnvelope<unknown[]>(snapshotRaw.deliberation_etudiants_rattrapage_get)
	return Array.isArray(legacy) ? legacy : null
}

/**
 * Snapshot : issue de la délibération snapshotée (feuille / liste).
 * Snapshots legacy 2e délib. : rattrapage_get = source 1re → retourne null (appel live requis).
 */
export function unwrapSnapshotRattrapageOutcome(
	snapshotRaw: Record<string, unknown> | null | undefined,
	isSecondeDeliberation: boolean
): unknown[] | null {
	if (!snapshotRaw) return null
	if (isSecondeDeliberation && !snapshotRaw.deliberation_etudiants_rattrapage_source_premiere_get) {
		return null
	}
	const outcome = unwrapApiEnvelope<unknown[]>(snapshotRaw.deliberation_etudiants_rattrapage_get)
	return Array.isArray(outcome) ? outcome : null
}

export type RattrapageOutcomeLoadResult = {
	items: unknown[]
	source: 'snapshot' | 'live'
	error?: string
}

/**
 * Charge l'issue rattrapage (feuille / modal / PDF) : snapshot prioritaire si disponible, sinon live.
 * Un tableau vide `[]` est une issue valide (ex. 2e délib. où tous sont admis).
 */
export async function loadRattrapageOutcomeItems(options: {
	isSecondeDeliberation: boolean
	snapshotRaw?: Record<string, unknown> | null
	fetchSnapshotRaw?: () => Promise<Record<string, unknown> | null | undefined>
	fetchLive: () => Promise<{ success: boolean; data?: unknown; message?: string }>
}): Promise<RattrapageOutcomeLoadResult> {
	let raw = options.snapshotRaw ?? null
	if (!raw && options.fetchSnapshotRaw) {
		raw = (await options.fetchSnapshotRaw()) ?? null
	}

	if (raw) {
		const snapItems = unwrapSnapshotRattrapageOutcome(raw, options.isSecondeDeliberation)
		if (Array.isArray(snapItems)) {
			return { items: snapItems, source: 'snapshot' }
		}
	}

	const result = await options.fetchLive()
	if (!result.success) {
		return {
			items: [],
			source: 'live',
			error: result.message || 'Erreur lors du chargement des rattrapages',
		}
	}
	return { items: Array.isArray(result.data) ? result.data : [], source: 'live' }
}

export function buildRattrapageMapsFromItems(items: unknown[]) {
	const inscriptionIds = new Set<number>()
	const ecByInscription = new Map<number, Set<number>>()

	for (const ec of items as any[]) {
		const ecId = Number(ec?.ec_id ?? ec?.ec?.id ?? ec?.ec?.ec_id ?? ec?.id ?? 0)
		if (!ecId || !Array.isArray(ec?.etudiants)) continue
		for (const etu of ec.etudiants) {
			const inscriptionId = Number(etu?.inscription_id)
			if (!inscriptionId) continue
			inscriptionIds.add(inscriptionId)
			if (!ecByInscription.has(inscriptionId)) ecByInscription.set(inscriptionId, new Set<number>())
			ecByInscription.get(inscriptionId)!.add(ecId)
		}
	}

	return { inscriptionIds, ecByInscription }
}

/** Nombre d'inscriptions distinctes concernées par le rattrapage (pas le nombre d'EC). */
export function countEtudiantsConcernesRattrapage(
	items: Array<{ etudiants?: unknown[] | null }> | null | undefined
): number {
	if (!items?.length) return 0
	const ids = new Set<number>()
	for (const ec of items) {
		if (!Array.isArray(ec?.etudiants)) continue
		for (const etu of ec.etudiants) {
			const inscriptionId = Number((etu as { inscription_id?: unknown })?.inscription_id)
			if (inscriptionId) ids.add(inscriptionId)
		}
	}
	return ids.size
}

/** True seulement s'il y a au moins un étudiant réellement en rattrapage. */
export function hasEtudiantsConcernesRattrapage(
	items: Array<{ etudiants?: unknown[] | null }> | null | undefined
): boolean {
	return countEtudiantsConcernesRattrapage(items) > 0
}
